import React, { createContext, useContext } from 'react';
import { ImpressionItem } from './ImpressionItem';
import { ViewMode } from '../Attachments/InlineAttachment';
import { useActiveUserId } from '../../../../../hooks';
import {
  useSendAgentFeedback,
  type SendAgentFeedbackInput,
} from '../../../../../hooks/useSendAgentFeedback';
import {
  useSuggestionFeedbackBatch,
  type RecordSuggestionHunkDecision,
} from './useSuggestionFeedbackBatch';

export type ImpressionEditorContextType = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  patient: fhir.Patient & { id: string };
  refresh: () => void;
  /** Refetch only the given clinical impression and update it in-place. */
  refetchImpression?: (impressionId: string) => Promise<void>;
  sendAgentFeedback: (input: SendAgentFeedbackInput) => Promise<unknown>;
  /**
   * Record a single suggestion hunk accept/reject decision. Decisions are
   * batched per suggestion trace and only sent to `POST /v1/agents-v2/feedback`
   * on {@link flushSuggestionFeedback}, keeping the agents Slack channel quiet.
   */
  recordSuggestionHunkDecision: RecordSuggestionHunkDecision;
  /** Flush accumulated suggestion hunk decisions as ≤2 consolidated feedback calls. */
  flushSuggestionFeedback: () => void;
  activeUserId: string;
  /** True while the suggest-agent is processing for the active impression. */
  isSuggesting: boolean;
  setIsSuggesting: (value: boolean) => void;
  /** When true the editor allows text selection and annotation even when not in edit mode. */
  canAnnotate: boolean;
  /** Controls whether knowledge-base citation widgets are rendered from markdown footnotes. */
  renderKnowledgeBaseCitations: boolean;
};

export const ImpressionEditorContext = createContext<
  ImpressionEditorContextType | undefined
>(undefined);

export const ImpressionEditorProvider: React.FC<{
  patient: fhir.Patient & { id: string };
  children: React.ReactNode;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  refresh: () => void;
  refetchImpression?: (impressionId: string) => Promise<void>;
  canAnnotate?: boolean;
  renderKnowledgeBaseCitations?: boolean;
}> = ({
  children,
  patient,
  isEditing,
  setIsEditing,
  activeImpression,
  viewMode,
  refresh,
  refetchImpression,
  canAnnotate = false,
  renderKnowledgeBaseCitations = true,
}) => {
  const activeUserId = useActiveUserId();
  const [isSuggesting, setIsSuggesting] = React.useState(false);
  const { sendAgentFeedback } = useSendAgentFeedback();
  const { recordSuggestionHunkDecision, flushSuggestionFeedback } =
    useSuggestionFeedbackBatch(sendAgentFeedback);
  return (
    <ImpressionEditorContext.Provider
      value={{
        isEditing,
        activeImpression,
        setIsEditing,
        viewMode,
        patient,
        refresh,
        refetchImpression,
        sendAgentFeedback,
        recordSuggestionHunkDecision,
        flushSuggestionFeedback,
        activeUserId,
        isSuggesting,
        setIsSuggesting,
        canAnnotate,
        renderKnowledgeBaseCitations,
      }}
    >
      {children}
    </ImpressionEditorContext.Provider>
  );
};

// Custom hook to use the ImpressionEditorContext
export const useImpressionEditorContext = (): ImpressionEditorContextType => {
  const context = useContext(ImpressionEditorContext);
  if (!context) {
    throw new Error(
      'useImpressionEditorContext must be used within an ImpressionEditorProvider',
    );
  }
  return context;
};

/** Same values as {@link useImpressionEditorContext}, or `undefined` outside a provider (e.g. report template Lexical editor). */
export const useMaybeImpressionEditorContext = ():
  | ImpressionEditorContextType
  | undefined => useContext(ImpressionEditorContext);
